import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Menu, UserCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { LanguageSwitcher } from './language-switcher';

interface NavigationHeaderProps {
  onMobileSidebarToggle?: () => void;
}

export function NavigationHeader({ onMobileSidebarToggle }: NavigationHeaderProps) {
  const { t } = useTranslation('layout');
  const [, setLocation] = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);

  const handleDashboardClick = () => {
    setLocation('/');
  };

  const handleLogout = () => {
    logout();
  };

  const storeName = user?.store?.name || 'My Charming Store';
  const storeLogo = user?.store?.logo;
  const userName = user?.name || 'User';
  const userEmail = user?.email || 'user@example.com';
  const userAvatar = user?.avatar || 'https://demo-mentor-s3.s3.ap-southeast-1.amazonaws.com/user.png';

  // Get user initials for avatar fallback
  const userInitials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <header className="sticky top-0 z-49 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left Section: Mobile Menu + Logo + Store Name */}
        <div className="flex flex-1 items-center gap-2 sm:gap-3">
          {/* Mobile/Tablet Menu Toggle */}
          {onMobileSidebarToggle && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onMobileSidebarToggle}
              aria-label={t('header.toggleSidebar')}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Store Logo */}
          <div className="flex flex-1 items-center gap-2 sm:gap-3">
            {storeLogo ? (
              <img
                src={storeLogo}
                alt={t('header.storeLogo', { storeName })}
                className="h-8 w-8 rounded-md border object-cover sm:h-9 sm:w-9"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground sm:h-9 sm:w-9">
                {storeName.charAt(0).toUpperCase()}
              </div>
            )}

            {/* Store Name & Subtitle */}
              <div className="flex flex-1 items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                  {storeName}
                </span>
              </div>
          </div>
        </div>

        {/* Right Section: User Profile Menu */}
        <div className="ml-auto flex items-center gap-2">
          {isAuthenticated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full p-0 sm:h-10 sm:w-10"
                  aria-label={t('header.openUserMenu')}
                >
                  <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                    <AvatarImage src={userAvatar} alt={userName} />
                    <AvatarFallback className="text-xs sm:text-sm">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{userName}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {userEmail}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsChangePasswordDialogOpen(true)}>
                  <UserCircle className="mr-2 h-4 w-4" />
                  <span>{t('header.changePassword')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <LanguageSwitcher />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('header.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

      </div>
      <ChangePasswordDialog
        open={isChangePasswordDialogOpen}
        onOpenChange={setIsChangePasswordDialogOpen}
      />
    </header>
  );
}
